/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LIST-FILTERS] HOW "ADJUST FILTERS" LOOKS — AMBER, AND NEVER A FILL.
 *
 *  The one non-status use of `warning` in the product, made at the user's direction: amber here
 *  honestly means "you are not seeing everything". Idle spends a 1px border, a 16px icon and the
 *  lightest wash; applied spends a stronger wash and a heavier weight. No token is added — a tone
 *  token under a Tailwind opacity modifier is [D4.5]'s rule for a tint, and this is one.
 *
 *  ⚠️ UTILITY STRINGS, NEVER `!important` — `publishControl.ts`' `UNAVAILABLE_LOOK` precedent.
 *  Tailwind sits in `@layer utilities`, above `@layer antd`, so these beat AntD's default button
 *  styling with nothing this repository forbids. No `dark:` either: every colour here is a custom
 *  property `ThemeScope` re-points, so the dark half is the token's, not a second class.
 *
 *  ⚠️ EVERY STATE'S WASH IS WRITTEN OUT. A base `border-*`, `bg-*` or `text-*` utility OVERRIDES
 *  AntD's own `:hover` and `:active` colours too — leave `hover:` off and the button stops
 *  answering the pointer; leave `hover:text-foreground` off and AntD's hover paints the label in
 *  the primary colour over an amber wash. So rest, hover and active are each stated: hover is
 *  always stronger than rest, and active is never weaker than hover — on the applied look the two
 *  are equal, and the press is answered by AntD's wave instead. `theme/listFiltersTint.test.ts`
 *  reads these strings and holds that order (`hover > rest`, `active >= hover`).
 *
 *  ⚠️ LITERAL CLASS STRINGS. Tailwind extracts candidates from source TEXT, so a class built by
 *  interpolation generates nothing — and the button renders with AntD's grey border, `tsc`,
 *  `eslint` and `vitest` all green. The tint test parses the alphas out of THIS file rather than
 *  retyping them, so a retuned wash is measured as it is written.
 *
 *  ⚠️ THE LABEL IS NEVER `text-warning` ON A WASH. Amber text over an amber wash falls to about
 *  3.9:1 in light — below body text's 4.5 — so the words stay `text-foreground` and only the
 *  icon and the border carry the hue, where the 3:1 non-text bar applies. The tint test pins that
 *  as a negative control.
 *
 *  ⚠️ AND THE BUTTON IS NEVER DISABLED. These utilities would override AntD's `:disabled` look and
 *  make a refused button read as live. If a disabled state is ever added, its look is `undefined`,
 *  exactly as `UNAVAILABLE_LOOK.refused` is.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const LIST_FILTERS_LOOK = {
  idle: 'border-warning bg-warning/10 text-foreground hover:border-warning hover:bg-warning/15 hover:text-foreground active:bg-warning/20',
  applied:
    'border-warning bg-warning/20 font-medium text-foreground hover:border-warning hover:bg-warning/25 hover:text-foreground active:bg-warning/25',
} as const satisfies Record<'idle' | 'applied', string>;

/** The icon carries the hue — the 3:1 non-text bar, which it clears on every wash above. */
export const LIST_FILTERS_ICON = 'size-4 text-warning';
